import { esc, unesc } from './kit.js';

// A small Markdown renderer for notes: headings, paragraphs (a single newline is a line break, as people expect in
// a note), bold, italic, strikethrough, inline and fenced code, links and bare URLs, images, quotes, nested lists
// with task boxes, tables and rules. All text is HTML-escaped before any markup is added, and links and images
// only take http(s), mailto (links) and same-site addresses, so a note can never inject markup or script.
// `u` is escaped text; it is checked the way the browser will read it, so tricks like control characters or
// entities in "javascript:" do not get through.
function safeUrl(u, schemes) {
  try { return schemes.includes(new URL(unesc(u), location.href).protocol) ? u : null; } catch { return null; }
}
const LINK_SCHEMES = ['http:', 'https:', 'mailto:'];
const IMAGE_SCHEMES = ['http:', 'https:'];
// Inline markup on one escaped line. Code spans, links and images are swapped for numbered tokens first, so
// nothing inside them is touched again; the tokens use characters markdown() strips from the input.
function inline(text) {
  const kept = [];
  const keep = (html) => `\u0001${kept.push(html) - 1}\u0002`;
  let t = text.replace(/(`+)(.+?)\1/g, (_, _q, code) => keep(`<code>${code}</code>`));
  t = t.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (m, alt, url) => {
    const src = safeUrl(url, IMAGE_SCHEMES);
    return src ? keep(`<img src="${src}" alt="${alt}" loading="lazy">`) : m;
  });
  // Links open in a new tab, except a link to a #section or a ?q=search, which the launcher takes on this page (see
  // "launcher from the address"); only those may have spaces in them, as in [Get started](?q=get started).
  const link = (url, label) => keep(/^(#|\?q=)/.test(url) ? `<a href="${url}">${label}</a>`
    : `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`);
  t = t.replace(/\[([^\]]+)\]\(((?:#|\?q=)[^)]+|[^)\s]+)\)/g, (m, label, url) => (safeUrl(url, LINK_SCHEMES) ? link(url, emphasis(label)) : m));
  t = t.replace(/&lt;((?:https?|mailto):[^\s&]+)&gt;/g, (m, url) => (safeUrl(url, LINK_SCHEMES) ? link(url, url) : m));
  t = t.replace(/\bhttps?:\/\/[^\s\u0001]+[^\s\u0001.,;:!?)'"]/g, (url) => (safeUrl(url, LINK_SCHEMES) ? link(url, url) : url));
  t = emphasis(t);
  return t.replace(/\u0001(\d+)\u0002/g, (_, i) => kept[i]);
}
function emphasis(t) {
  return t
    .replace(/(\*\*|__)(?=\S)(.+?)(?<=\S)\1/g, '<strong>$2</strong>')
    .replace(/(^|[^\w*])\*(?=\S)(.+?)(?<=\S)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/(^|[^\w])_(?=\S)(.+?)(?<=\S)_(?!\w)/g, '$1<em>$2</em>')
    .replace(/~~(?=\S)(.+?)(?<=\S)~~/g, '<del>$1</del>');
}
const MD = {
  fence: /^\s*(```|~~~)/,
  heading: /^(#{1,6})\s+(.*?)\s*#*\s*$/,
  rule: /^\s*([-*_])(\s*\1){2,}\s*$/,
  quote: /^\s*>/,
  item: /^(\s*)([-*+]|\d+[.)])\s+(.*)$/,
  tableSep: /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/,
};
const TASK = /^\[([ xX])\]\s+(.*)$/;
// A task item's line in the source, quote markers included: group 1 runs up to its box.
export const TASK_LINE = /^((?:\s*>)*\s*(?:[-*+]|\d+[.)])\s+)\[[ xX]\]/;
const isTable = (lines, i) => lines[i].includes('|') && MD.tableSep.test(lines[i + 1] ?? '') && lines[i + 1].includes('-');
const startsBlock = (lines, i) => MD.fence.test(lines[i]) || MD.heading.test(lines[i]) || MD.rule.test(lines[i])
  || MD.quote.test(lines[i]) || MD.item.test(lines[i]) || isTable(lines, i);
// Task boxes carry the number of their source line (data-line) and come out disabled; renderNote enables them for
// editors and rewrites that line when one is ticked. `at` maps each of `lines` to its line in the source.
export function markdown(src) {
  return blocks(src.replace(/\r\n?/g, '\n').replace(/[\u0001\u0002]/g, '').split('\n'));
}
function blocks(lines, at = lines.map((_, i) => i)) {
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    let m;
    if (!line.trim()) { i++; continue; }
    if ((m = line.match(MD.fence))) {
      const body = [];
      for (i++; i < lines.length && !lines[i].trim().startsWith(m[1]); i++) body.push(lines[i]);
      i++;
      out.push(`<pre><code>${esc(body.join('\n'))}</code></pre>`);
    } else if ((m = line.match(MD.heading))) {
      out.push(`<h${m[1].length}>${inline(esc(m[2]))}</h${m[1].length}>`);
      i++;
    } else if (MD.rule.test(line)) {
      out.push('<hr>');
      i++;
    } else if (MD.quote.test(line)) {
      const body = [], bodyAt = [];
      for (; i < lines.length && MD.quote.test(lines[i]); i++) { body.push(lines[i].replace(/^\s*>\s?/, '')); bodyAt.push(at[i]); }
      out.push(`<blockquote>${blocks(body, bodyAt)}</blockquote>`);
    } else if (MD.item.test(line)) {
      const body = [], bodyAt = [];
      for (; i < lines.length && (MD.item.test(lines[i]) || (/^\s+\S/.test(lines[i]) && body.length)); i++) { body.push(lines[i]); bodyAt.push(at[i]); }
      out.push(list(body, bodyAt));
    } else if (isTable(lines, i)) {
      const cells = (l) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
      const align = cells(lines[i + 1]).map((c) => (c.endsWith(':') ? (c.startsWith(':') ? 'center' : 'right') : 'left'));
      const row = (l, tag) => `<tr>${cells(l).map((c, k) => `<${tag} style="text-align:${align[k] || 'left'}">${inline(esc(c))}</${tag}>`).join('')}</tr>`;
      const rows = [];
      for (i += 2; i < lines.length && lines[i].includes('|') && lines[i].trim(); i++) rows.push(row(lines[i], 'td'));
      out.push(`<table><thead>${row(line, 'th')}</thead><tbody>${rows.join('')}</tbody></table>`);
    } else {
      const para = [];
      for (; i < lines.length && lines[i].trim() && (!para.length || !startsBlock(lines, i)); i++) para.push(inline(esc(lines[i].trim())));
      out.push(`<p>${para.join('<br>')}</p>`);
    }
  }
  return out.join('');
}
// One list: its first line sets the indent; deeper item lines nest, other deeper lines continue the item above.
function list(lines, at) {
  const base = lines[0].match(MD.item)[1].length;
  const first = lines[0].match(MD.item)[2];
  const ordered = /\d/.test(first);
  const items = [];
  lines.forEach((l, k) => {
    const m = l.match(MD.item);
    if (m && m[1].length <= base) items.push({ text: m[3], line: at[k], more: [], sub: [], subAt: [] });
    else if (m || items.at(-1).sub.length) { items.at(-1).sub.push(l); items.at(-1).subAt.push(at[k]); }
    else items.at(-1).more.push(l.trim());
  });
  const html = items.map(({ text, line, more, sub, subAt }) => {
    const task = text.match(TASK);
    const body = [task ? task[2] : text, ...more].map((t) => inline(esc(t))).join('<br>');
    const box = task ? `<input type="checkbox" data-line="${line}" disabled${task[1] === ' ' ? '' : ' checked'}> ` : '';
    return `<li${task ? ' class="task"' : ''}>${box}${body}${sub.length ? list(sub, subAt) : ''}</li>`;
  }).join('');
  const start = ordered && parseInt(first, 10) !== 1 ? ` start="${parseInt(first, 10)}"` : '';
  return ordered ? `<ol${start}>${html}</ol>` : `<ul>${html}</ul>`;
}
